import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { apiClient, socketClient } from '../../services/api';
import dispatchService from '../../services/dispatchService';
import Logo from '../ui/Logo';
import { LanguageCurrencyPicker } from './LanguageCurrencyPicker';
import {
    startDashboardTour,
    startProjectsTour,
    startFinancialTour,
    startWorkspaceTour,
    startAcademyTour,
    startTalentTour,
    startSkillsTour,
    startSettingsTour,
    startThreeDHubTour,
    startUnityBotTour,
    startHRPayrollTour,
    startClientPortalTour,
    startDispatchTour,
} from '../onboarding';

interface Notification {
    _id: string;
    id?: string;
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'talent_qualification' | 'profile_incomplete' | 'welcome' | 'system' | 'reminder' | 'application' | 'comment';
    isRead: boolean;
    read?: boolean;
    timestamp?: Date;
    createdAt?: string;
    priority?: 'low' | 'medium' | 'high';
    link?: string;
}

interface AppHeaderProps {
    title?: string;
}

const AppHeader: React.FC<AppHeaderProps> = ({ title }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    const [showSettings, setShowSettings] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const [isProcessing, setIsProcessing] = useState(false);
    const [applicationCount, setApplicationCount] = useState(0);

    // Determine which tour to show based on current page
    const getCurrentTour = () => {
        const path = location.pathname.toLowerCase();
        if (path.includes('dashboard')) return { name: 'Dashboard Tour', action: startDashboardTour };
        if (path.includes('projects')) return { name: 'Projects Tour', action: startProjectsTour };
        if (path.includes('financial')) return { name: 'Financial Tour', action: startFinancialTour };
        if (path.includes('workspace')) return { name: 'Workspace Tour', action: startWorkspaceTour };
        if (path.includes('academy')) return { name: 'Academy Tour', action: startAcademyTour };
        if (path.includes('talent')) return { name: 'Talent Tour', action: startTalentTour };
        if (path.includes('skills')) return { name: 'Skills Tour', action: startSkillsTour };
        if (path.includes('settings')) return { name: 'Settings Tour', action: startSettingsTour };
        if (path.includes('3dhub')) return { name: '3D Hub Tour', action: startThreeDHubTour };
        if (path.includes('unitybot')) return { name: 'AI Assistant Tour', action: startUnityBotTour };
        if (path.includes('hr')) return { name: 'HR & Payroll Tour', action: startHRPayrollTour };
        if (path.includes('client')) return { name: 'Client Portal Tour', action: startClientPortalTour };
        if (path.includes('dispatch')) return { name: 'Dispatch Tour', action: startDispatchTour };
        return { name: 'Platform Tour', action: startDashboardTour }; // Default
    };

    const currentTour = getCurrentTour();

    // Fetch user notifications from backend
    useEffect(() => {
        if (!user) return;

        const fetchNotifications = async () => {
            try {
                const response = await apiClient.getUserNotifications();
                if (response && response.notifications) {
                    setNotifications(response.notifications);
                }
            } catch (error) {
                console.error('Failed to fetch notifications:', error);
            }
        };

        fetchNotifications();
        const intervalId = setInterval(fetchNotifications, 30000);

        // Socket listener for real-time notification refresh
        const unsubscribe = socketClient.subscribe('task_match', () => {
            fetchNotifications();
        });

        return () => {
            clearInterval(intervalId);
            unsubscribe();
        };
    }, [user]);

    // Fetch potential applications count
    useEffect(() => {
        if (!user) return;

        const fetchApps = async () => {
            try {
                const response = await dispatchService.getReceivedApplications();
                if (response.success) {
                    const pending = response.applications.filter(app => app.status === 'pending').length;
                    setApplicationCount(pending);
                }
            } catch (error) {
                // Silently fail or log
                console.error('Failed to fetch apps count', error);
            }
        };

        fetchApps();
        const intervalId = setInterval(fetchApps, 30000);

        // Socket listener for app updates
        const unsubscribeUpdate = socketClient.subscribe('application_update', () => {
            fetchApps();
        });

        return () => {
            clearInterval(intervalId);
            unsubscribeUpdate();
        };
    }, [user]);

    // Real qualification check using API
    useEffect(() => {
        if (!user) return;

        const checkQualification = async () => {
            try {
                // Prevent duplicate checks/notifications if already present
                if (notifications.some(n => n.type === 'talent_qualification')) return;

                const userId = (user as any)._id || (user as any).id;
                if (!userId) {
                    console.warn('User ID not found for eligibility check');
                    return;
                }

                const response = await apiClient.checkEligibility(userId);

                if (response && response.eligible && response.status === 'qualified') {
                    const newNotif: Notification = {
                        _id: `qual-${Date.now()}`,
                        title: '🌟 You are Qualified!',
                        message: response.reason || 'Congratulations! You met the criteria to join our exclusive Talent Platform.',
                        type: 'talent_qualification',
                        isRead: false,
                        createdAt: new Date().toISOString()
                    };
                    setNotifications(prev => [newNotif, ...prev]);
                }
            } catch (error) {
                console.error('Failed to check eligibility:', error);
            }
        };

        checkQualification();
        // Periodic re-check every 5 minutes
        const intervalId = setInterval(checkQualification, 300000);
        return () => clearInterval(intervalId);
    }, [user, notifications]);

    const handleAcceptTalent = async (notificationId: string) => {
        if (!user) return;
        setIsProcessing(true);
        try {
            await apiClient.createTalentProfile((user as any)._id || (user as any).id, {
                name: `${user.firstName} ${user.lastName}`,
                headline: 'New Talent',
                bio: 'I am a new member of the Talent Network.',
                location: 'Remote',
                status: 'active',
                avatar: (user as any).avatar,
            });

            // Remove notification
            setNotifications(prev => prev.filter(n => n._id !== notificationId));

            // Show success
            alert('Welcome to the Talent Network! Your profile has been created.');
            navigate('/talent');
        } catch (error) {
            console.error('Failed to join talent network:', error);
            alert('Failed to join at this time. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRejectTalent = (notificationId: string) => {
        setNotifications(prev => prev.filter(n => n._id !== notificationId));
    };

    // Mock handlers for settings
    const handleLockSite = () => {
        setIsLocked(true);
        setShowSettings(false);
    };

    const handleUnlock = (e: React.FormEvent) => {
        e.preventDefault();
        setIsLocked(false);
    };

    // 3FA Mock Toggle
    const [is3FAEnabled, setIs3FAEnabled] = useState(false);
    const toggle3FA = () => setIs3FAEnabled(!is3FAEnabled);

    if (isLocked) {
        return (
            <div className="fixed inset-0 z-[100] bg-premium-wood-dark/95 backdrop-blur-xl flex flex-col items-center justify-center p-4">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-black/40 p-8 rounded-2xl border border-premium-gold/30 flex flex-col items-center max-w-sm w-full"
                >
                    <div className="mb-6"><Logo size="lg" /></div>
                    <h2 className="text-2xl text-premium-gold font-serif mb-2">Site Locked</h2>
                    <p className="text-white/60 text-sm mb-6 text-center">Please enter your password to resume.</p>

                    <form onSubmit={handleUnlock} className="w-full">
                        <input
                            type="password"
                            placeholder="Enter Password"
                            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white mb-4 focus:border-premium-gold outline-none"
                            autoFocus
                        />
                        <button
                            type="submit"
                            className="w-full bg-premium-gold text-premium-wood-dark font-bold py-3 rounded-lg hover:bg-premium-gold-light transition-colors"
                        >
                            Unlock
                        </button>
                    </form>
                </motion.div>
            </div>
        );
    }

    const unreadCount = notifications.filter(n => !n.isRead && !n.read).length;

    return (
        <>
            <header className="relative z-50 flex items-center justify-between px-3 md:px-12 py-4 max-w-7xl mx-auto border-b border-premium-gold/20 bg-premium-wood-dark/50 backdrop-blur-sm sticky top-0">
                <div className="flex items-center gap-2 md:gap-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/')}>
                    <Logo variant="sidebar" size="sm" />
                    {title && <div className="hidden md:block w-px h-6 bg-white/20 mx-2"></div>}
                    {title && <h1 className="hidden md:block text-lg font-serif text-premium-gold font-bold whitespace-nowrap">{title}</h1>}
                </div>

                {/* Main Navigation */}
                <nav className="hidden md:flex items-center gap-6">
                    <button id="nav-dashboard" onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white transition-colors">{t('common.dashboard')}</button>
                    <button id="nav-dispatch" onClick={() => navigate('/dispatch')} className="text-white/70 hover:text-white transition-colors">{t('common.workspace')}</button>
                    <button id="nav-financial" onClick={() => navigate('/financial')} className="text-white/70 hover:text-white transition-colors">Financial</button>
                    <button id="nav-workspace" onClick={() => navigate('/workspace')} className="text-white/70 hover:text-white transition-colors">{t('common.workspace')}</button>
                    <button id="nav-leaderboard" onClick={() => navigate('/leaderboard')} className="text-white/70 hover:text-white transition-colors">Leaderboards</button>
                </nav>

                <div className="flex items-center gap-2 md:gap-6">
                    <LanguageCurrencyPicker />
                    {/* Dispatch/Jobs Icon */}
                    <button
                        id="header-dispatch"
                        onClick={() => navigate('/dispatch/applications')}
                        className="p-2 rounded-full text-white/70 hover:text-white transition-all duration-300 relative hover:bg-white/5"
                        title="Job Board"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {applicationCount > 0 && (
                            <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-premium-gold rounded-full border border-[#1a1a1a] animate-pulse"></span>
                        )}
                    </button>

                    {/* Notifications Bell */}
                    <div className="relative">
                        <button
                            id="header-notifications"
                            onClick={() => setShowNotifications(!showNotifications)}
                            className={`p-2 rounded-full transition-all duration-300 relative ${showNotifications ? 'text-premium-gold' : 'text-white/70 hover:text-white'}`}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                            {unreadCount > 0 && (
                                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-[#1a1a1a]"></span>
                            )}
                        </button>

                        <AnimatePresence>
                            {showNotifications && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    className="absolute right-0 mt-4 w-80 md:w-96 bg-[#1a1a1a] border border-premium-gold/20 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] overflow-hidden z-[60]"
                                >
                                    <div className="p-4 border-b border-white/5 flex justify-between items-center">
                                        <h3 className="text-white font-bold">Notifications</h3>
                                        <div className="flex gap-3 items-center">
                                            <span className="text-xs text-premium-gold cursor-pointer hover:underline" onClick={() => navigate('/notifications')}>View all</span>
                                            <span className="text-[10px] text-white/30">|</span>
                                            <span className="text-xs text-premium-gold cursor-pointer hover:underline" onClick={() => setNotifications(prev => prev.map(n => ({ ...n, read: true, isRead: true })))}>Mark all read</span>
                                        </div>
                                    </div>

                                    <div className="max-h-96 overflow-y-auto">
                                        {notifications.length === 0 ? (
                                            <div className="p-8 text-center text-white/40 text-sm">
                                                No new notifications
                                            </div>
                                        ) : (
                                            notifications.map(notif => (
                                                <div
                                                    key={notif._id || notif.id}
                                                    onClick={() => {
                                                        if (!notif.isRead && !notif.read) {
                                                            apiClient.markNotificationAsRead(notif._id || notif.id || '');
                                                            setNotifications(prev => prev.map(n =>
                                                                (n._id === notif._id || n.id === notif.id) ? { ...n, isRead: true, read: true } : n
                                                            ));
                                                        }
                                                        navigate('/notifications');
                                                        setShowNotifications(false);
                                                    }}
                                                    className={`p-4 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${!notif.isRead && !notif.read ? 'bg-white/5' : ''}`}
                                                >
                                                    <div className="flex gap-3">
                                                        <div className="text-2xl mt-1">
                                                            {notif.type === 'talent_qualification' ? '🏆' :
                                                                notif.type === 'profile_incomplete' ? '📝' :
                                                                    notif.type === 'application' ? '💼' :
                                                                        notif.type === 'comment' ? '💬' :
                                                                            '📢'}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex justify-between items-start mb-1">
                                                                <h4 className="font-bold text-white text-sm">{notif.title}</h4>
                                                                <span className="text-[10px] text-white/30">
                                                                    {notif.createdAt ? new Date(notif.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                                                                        notif.timestamp ? notif.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-white/70 mb-3">{notif.message}</p>

                                                            {notif.type === 'talent_qualification' && (
                                                                <div className="flex gap-2 mt-2">
                                                                    <button
                                                                        onClick={() => handleAcceptTalent(notif._id || notif.id || '')}
                                                                        disabled={isProcessing}
                                                                        className="flex-1 py-1.5 bg-premium-gold text-premium-wood-dark text-xs font-bold rounded hover:bg-white disabled:opacity-50 transition-colors"
                                                                    >
                                                                        {isProcessing ? 'Joining...' : 'Accept & Join'}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleRejectTalent(notif._id || notif.id || '')}
                                                                        className="flex-1 py-1.5 border border-white/20 text-white text-xs font-bold rounded hover:bg-white/10 transition-colors"
                                                                    >
                                                                        Reject
                                                                    </button>
                                                                </div>
                                                            )}

                                                            {notif.type === 'profile_incomplete' && (
                                                                <button
                                                                    onClick={() => {
                                                                        navigate(notif.link || '/profile');
                                                                        setShowNotifications(false);
                                                                    }}
                                                                    className="mt-2 py-1.5 px-3 bg-premium-gold/20 hover:bg-premium-gold/30 text-premium-gold text-xs font-bold rounded transition-colors"
                                                                >
                                                                    Complete Profile
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* User Profile Dropdown */}
                    <div className="relative">
                        <button
                            id="header-profile"
                            onClick={() => setShowSettings(!showSettings)}
                            className="flex items-center gap-1.5 md:gap-3 px-2 md:px-4 py-1.5 md:py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-premium-gold/30 transition-all group overflow-hidden"
                        >
                            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-premium-gold to-premium-gold-dark text-premium-wood-dark font-bold flex items-center justify-center text-sm shadow-[0_0_15px_rgba(212,175,55,0.3)] flex-shrink-0 overflow-hidden">
                                {user?.avatar ? (
                                    <img src={user.avatar} alt="Profile" className="w-full h-full object-cover" />
                                ) : (
                                    <span>{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
                                )}
                            </div>
                            <div className="hidden md:flex flex-col items-start">
                                <span className="text-white text-sm font-medium leading-none">{user?.firstName} {user?.lastName}</span>
                                <span className="text-premium-gold text-[10px] uppercase tracking-wider mt-0.5">{user?.role || 'Member'}</span>
                            </div>
                            <svg className={`w-4 h-4 text-white/50 transition-transform duration-300 ${showSettings ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        <AnimatePresence>
                            {showSettings && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    className="absolute right-0 mt-4 w-72 bg-[#1a1a1a] border border-premium-gold/20 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] overflow-hidden z-[60]"
                                >
                                    <div className="p-4 border-b border-white/5 bg-premium-wood-dark/50">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 rounded-full bg-premium-gold flex items-center justify-center text-premium-wood-dark font-bold overflow-hidden">
                                                {user?.avatar ? (
                                                    <img src={user.avatar} alt="Profile" className="w-full h-full object-cover" />
                                                ) : (
                                                    <span>{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
                                                )}
                                            </div>
                                            <div>
                                                <div className="text-white font-bold">{user?.username}</div>
                                                <div className="text-xs text-white/60">{user?.email}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-2 space-y-1">
                                        <button onClick={() => { navigate('/settings'); setShowSettings(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                            <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">⚙️</span>
                                            <div className="flex-1">
                                                <div className="text-sm font-medium">Settings</div>
                                                <div className="text-[10px] text-white/40">Edit profile & preferences</div>
                                            </div>
                                        </button>

                                        <button onClick={() => { navigate('/talent'); setShowSettings(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                            <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">👤</span>
                                            <div className="flex-1">
                                                <div className="text-sm font-medium">My Public Profile</div>
                                                <div className="text-[10px] text-white/40">View as others see you</div>
                                            </div>
                                        </button>

                                        <button onClick={() => { navigate('/financial'); setShowSettings(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                            <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">💰</span>
                                            <div className="flex-1">
                                                <div className="text-sm font-medium">My Vault</div>
                                                <div className="text-[10px] text-white/40">Manage your earnings & assets</div>
                                            </div>
                                        </button>

                                        <button onClick={handleLockSite} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                            <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">🔒</span>
                                            <div className="flex-1">
                                                <div className="text-sm font-medium">Lock Site</div>
                                                <div className="text-[10px] text-white/40">Require password to resume</div>
                                            </div>
                                        </button>

                                        <button onClick={toggle3FA} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                            <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">🛡️</span>
                                            <div className="flex-1">
                                                <div className="text-sm font-medium flex justify-between items-center">
                                                    Enable 3FA
                                                    <div className={`w-8 h-4 rounded-full relative transition-colors ${is3FAEnabled ? 'bg-premium-gold' : 'bg-white/20'}`}>
                                                        <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${is3FAEnabled ? 'left-4.5' : 'left-0.5'}`} style={{ left: is3FAEnabled ? '18px' : '2px' }} />
                                                    </div>
                                                </div>
                                                <div className="text-[10px] text-white/40">Three-Factor Authentication</div>
                                            </div>
                                        </button>

                                        <button className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                            <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">🔑</span>
                                            <div className="flex-1">
                                                <div className="text-sm font-medium">Change Password</div>
                                            </div>
                                        </button>

                                        <button onClick={() => { currentTour.action(); setShowSettings(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                            <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">🎯</span>
                                            <div className="flex-1">
                                                <div className="text-sm font-medium">{currentTour.name}</div>
                                                <div className="text-[10px] text-white/40">Learn about this page's features</div>
                                            </div>
                                        </button>
                                    </div>

                                    <div className="p-2 border-t border-white/5 bg-red-500/5">
                                        <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors text-left">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
                                            <span className="text-sm font-medium">Sign Out</span>
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </header>


        </>
    );
};

export default AppHeader;
